import { ApiResponse, Clinic, PaginationResponse } from '@magnetar/admin-api-interfaces'
import { useAuth } from './useAuth'
import { useGet } from './useFetch'

// Define the PeopleFlowDevice interface
export interface PeopleFlowDevice {
  people_flow_device_id: number
  clinic_id: number
  name: string
  ip_address: string
  is_active: boolean
  created_at: string
  updated_at: string
  clinic?: Clinic
}

interface UsePeopleFlowDevicesParams {
  page?: number
  pageSize?: number
  clinic_id?: number
  name?: string
  ip_address?: string
  is_active?: boolean
}

export function usePeopleFlowDevices({
  page = 1,
  pageSize = 10,
  clinic_id,
  name,
  ip_address,
  is_active,
}: UsePeopleFlowDevicesParams = {}) {
  const { token } = useAuth()

  // Build query parameters
  const queryParams = new URLSearchParams({
    page: page.toString(),
    pageSize: pageSize.toString(),
  })

  if (clinic_id !== undefined) queryParams.append('clinic_id', clinic_id.toString())
  if (name) queryParams.append('name', name)
  if (ip_address) queryParams.append('ip_address', ip_address)
  if (is_active !== undefined) queryParams.append('is_active', is_active.toString())

  const { data, error, mutate } = useGet<ApiResponse<PaginationResponse<PeopleFlowDevice>>>(
    token ? `/api/people-flow-devices?${queryParams.toString()}` : null,
    undefined,
    token ?? '',
  )

  return {
    devices: data?.data?.items,
    pagination: data?.data
      ? {
          page: data.data.page,
          pageSize: data.data.pageSize,
          total: data.data.total,
          totalPages: data.data.totalPages,
        }
      : undefined,
    isLoading: !error && !data,
    isError: error,
    mutate,
  }
}

export function usePeopleFlowDevicesByClinicId(clinicId?: number) {
  const { token } = useAuth()

  const { data, error, mutate } = useGet<ApiResponse<PeopleFlowDevice[]>>(
    token && clinicId ? `/api/clinics/${clinicId}/people-flow-devices` : null,
    undefined,
    token ?? '',
  )

  return {
    devices: data?.data,
    isLoading: !error && !data,
    isError: error,
    mutate,
  }
}

export function usePeopleFlowDeviceById(id?: number) {
  const { token } = useAuth()

  const { data, error, mutate } = useGet<ApiResponse<PeopleFlowDevice>>(
    token && id ? `/api/people-flow-devices/${id}` : null,
    undefined,
    token ?? '',
  )

  return {
    device: data?.data,
    isLoading: !error && !data,
    isError: error,
    mutate,
  }
}
